import { BooleanInput, NumberInput, coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { isEqual } from 'lodash';

import { ITime } from 'src/app/components/input-controls/simple-time-input/simple-time-input.component';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { UNUSED_ARGS } from 'src/app/utility/unused-arguments';
import { stepValidator } from 'src/app/utility/validators/step.validator';
import { SubSink } from 'subsink';
import { SimpleTimeInputComponent } from '../../../../input-controls/simple-time-input/simple-time-input.component';


/**
 * Input for shaker profile values.
 *
 * Note: time values are passed as seconds.
 */
@Component({
  selector: 'app-shaker-profile-value-input',
  imports: [FormsModule,
    ReactiveFormsModule, MaterialImportsModule,
    SimpleTimeInputComponent,],
  templateUrl: './shaker-profile-value-input.component.html',
  styleUrls: ['./shaker-profile-value-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShakerProfileValueInputComponent implements OnChanges, OnInit, OnDestroy {

  /**
   * The form control for the input.
   *
   * @private
   */
  protected control = new FormControl<unknown>(undefined, [Validators.required]);


  /**
   * Type of the input
   */
  @Input({ required: true })
  public set type(value: ShakerProfileValueInputComponent['_type'] | undefined) {
    this._type = value;

    if (value !== 'TIME') {
      this.min = 0;
    }
  }

  public get type() {
    return this._type;
  }

  /**
   * backing field for the `type`-input
   */
  private _type?: 'CARBON_DIOXIDE' | 'RPM' | 'HUMIDITY' | 'TEMPERATURE' | 'TIME';


  /**
   * The value of the input.
   */
  @Input()
  public set value(value: number | ITime | undefined) {
    // See ngOnChanges
    UNUSED_ARGS(value);
  }


  /**
   * For number inputs: the minimum value
   */
  @Input()
  public set min(value: NumberInput) {
    if (this.minValidator) {
      this.control.removeValidators(this.minValidator);
    }

    if (value !== undefined && value !== null) {
      this.minValidator = Validators.min(coerceNumberProperty(value));
      this.control.addValidators(this.minValidator);
    }

    this.control.updateValueAndValidity();
  }

  /**
   * Keeps track of the minimum validator function
   */
  protected minValidator?: ValidatorFn;


  /**
   * For number inputs: the maximum value
   */
  @Input()
  public set max(value: NumberInput) {
    if (this.maxValidator) {
      this.control.removeValidators(this.maxValidator);
    }

    if (value !== undefined && value !== null) {
      this.maxValidator = Validators.max(coerceNumberProperty(value));
      this.control.addValidators(this.maxValidator);
    }

    this.control.updateValueAndValidity();
  }


  /**
   * Keeps track of the maximum validator function
   */
  protected maxValidator?: ValidatorFn;


  /**
   * For number inputs: the maximum value
   */
  @Input()
  public set step(value: NumberInput) {
    if (this.stepValidator) {
      this.control.removeValidators(this.stepValidator);
    }

    if (value !== undefined && value !== null) {
      this.stepValidator = stepValidator(coerceNumberProperty(value));
      this.control.addValidators(this.stepValidator);
    }

    this.control.updateValueAndValidity();
  }


  /**
   * Keeps track of the maximum validator function
   */
  protected stepValidator?: ValidatorFn;


  @Input()
  public set disabled(value: BooleanInput) {
    const coerced = coerceBooleanProperty(value);
    this.setDisabledState!(coerced);
  }

  @Input({ required: true })
  public unit: string | undefined;


  /**
   * To support the [(value)]-binding.
   */
  @Output()
  public valueChange = new EventEmitter<any>();


  /**
   * Tracks rxjs subscriptions.
   */
  private readonly subsink = new SubSink();


  private _errorMessages$ = this.control.statusChanges.pipe();


  /**
   * @private
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      // `null` indicates an invalid value, which we want to ignore
      if (changes.value.currentValue === null) {
        return;
      }

      // No change? No action required.
      if (isEqual(changes.value.currentValue, changes.value.previousValue)) {
        return;
      }

      let result = changes.value.currentValue;

      if (this.type === 'TIME') {
        result = this.secondsToITime(result);
      }

      this.control.setValue(result);
    }
  }


  /**
   * @private
   */
  public ngOnInit(): void {
    // Process the value changes
    this.subsink.sink = this.control.valueChanges.subscribe(value => {
      if (this.control.valid) {
        let result: number;

        if (this.type === 'TIME' && value !== null) {
          const time = value as ITime;
          result = Number(time.days) * 3600 * 24 + Number(time.hours) * 3600 + Number(time.minutes) * 60 + Number(time.seconds);
        } else {
          // Note: right now, everything is considered a number in here
          result = +(value as string);
        }

        this._onChange(result);
      } else {
        this._onChange(null);
      }
    });
  }


  /**
   * @private
   */
  public ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }


  /**
   * Notify listeners about a value change.
   *
   * @param value
   */
  private _onChange(value: any): void {
    this.valueChange.emit(value);
  }


  /**
   * see {@link https://angular.io/api/forms/ControlValueAccessor#setdisabledstate}
   *
   * @param isDisabled
   */
  private setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }


  private secondsToITime(value: number): ITime {
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);
    const seconds = value % 60;

    return { hours, minutes, seconds, days: 0 };
  }


  /**
   * @private
   * @param value The value to use instead of the current value
   */
  protected _confirmSuggestedValue(value: any) {
    this.control.setValue(value);
  }
}
